import { useCallback, useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
  Position,
  type EdgeProps,
  type Edge,
} from '@xyflow/react';
import { makeStyles, tokens, Tooltip, mergeClasses } from '@coreai/fluentui-react';
import { AddFilled, BotFilled } from '@fluentui/react-icons';
import { useIsHorizontalLayout } from '@/hooks/useLayoutOrientation';
import { EdgePillButton } from './EdgePillButton';

export type TerminalEdgeData = {
  onAddClick?: (sourceId: string) => void;
  onAddAgentClick?: (sourceId: string) => void;
};

export type TerminalEdgeType = Edge<TerminalEdgeData, 'terminal'>;

const useStyles = makeStyles({
  addButton: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    padding: '0',
    margin: 0,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopStyle: 'dashed',
    borderRightStyle: 'dashed',
    borderBottomStyle: 'dashed',
    borderLeftStyle: 'dashed',
    borderTopWidth: '2px',
    borderRightWidth: '2px',
    borderBottomWidth: '2px',
    borderLeftWidth: '2px',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    ':hover': {
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
    },
    ':focus-visible': {
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      outline: 'none',
    },
  },
  addButtonTrue: {
    borderTopColor: tokens.colorPaletteGreenBorder2,
    borderRightColor: tokens.colorPaletteGreenBorder2,
    borderBottomColor: tokens.colorPaletteGreenBorder2,
    borderLeftColor: tokens.colorPaletteGreenBorder2,
  },
  addButtonFalse: {
    borderTopColor: tokens.colorPaletteRedBorder2,
    borderRightColor: tokens.colorPaletteRedBorder2,
    borderBottomColor: tokens.colorPaletteRedBorder2,
    borderLeftColor: tokens.colorPaletteRedBorder2,
  },
  addButtonLoop: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
  },
  addIcon: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground3,
  },
  addIconTrue: {
    color: tokens.colorPaletteGreenForeground1,
  },
  addIconFalse: {
    color: tokens.colorPaletteRedForeground1,
  },
  addIconLoop: {
    color: tokens.colorBrandForeground1,
  },
  // Anchor that positions the agent button beside the dashed +. The button
  // itself manages its own reveal animation; this wrapper only places it so
  // enabling the affordance never shifts the primary +.
  agentButtonAnchor: {
    position: 'absolute',
    left: '100%',
    top: '50%',
    marginLeft: tokens.spacingHorizontalS,
    transform: 'translateY(-50%)',
  },
  agentIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
  },
  // Transparent hover bridge across the 8px gap between the + and the agent
  // button. Without it the cursor briefly leaves the wrapper while traversing
  // the gap (the agent button is positioned outside the wrapper's 32x32 box)
  // and collapses the reveal. Becomes pointer-active only while revealed so
  // it never blocks unrelated clicks.
  hoverBridge: {
    position: 'absolute',
    left: 'calc(100% - 2px)',
    top: '50%',
    width: '12px',
    height: '24px',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  hoverBridgeActive: {
    pointerEvents: 'auto',
  },
});

/**
 * Terminal edge component that shows a + button at the end
 * Used for adding actions at the end of the workflow
 */
export const TerminalEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  sourceHandleId,
  style,
  source,
  data,
}: EdgeProps<TerminalEdgeType>) => {
  const styles = useStyles();
  const isHorizontal = useIsHorizontalLayout();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const showAgentVariant = !!data?.onAddAgentClick;
  const isAgentRevealed = showAgentVariant && (isHovered || isFocused);

  const isTrueBranch = sourceHandleId === 'true';
  const isFalseBranch = sourceHandleId === 'false';
  const isLoopBranch = sourceHandleId === 'loop';

  // If the source handle exits perpendicular to the flow direction (e.g. an If
  // node's true/false handles at Top/Bottom in a horizontal layout), draw a
  // gentle parabolic bezier so the edge bows out of the handle and curves
  // toward the phantom target. Otherwise keep the edge perfectly straight
  // along the flow axis.
  const sourceIsPerpendicular = isHorizontal
    ? sourcePosition === Position.Top || sourcePosition === Position.Bottom
    : sourcePosition === Position.Left || sourcePosition === Position.Right;

  let edgePath: string;
  let labelX: number;
  let labelY: number;

  if (sourceIsPerpendicular) {
    // Build a custom parabolic path that bows clearly out of the perpendicular
    // handle (e.g. If true/false at Top/Bottom of the diamond in horizontal
    // layout) and sweeps toward the phantom target without snapping back to a
    // straight line. We compute an explicit cubic bezier so the curvature is
    // pronounced regardless of distance.
    const isTopOrLeft = sourcePosition === Position.Top || sourcePosition === Position.Left;
    // Pull source control point further perpendicular (away from node body)
    const perpOffset = 60;
    const sourceCtrlX = isHorizontal ? sourceX : sourceX + (isTopOrLeft ? -perpOffset : perpOffset);
    const sourceCtrlY = isHorizontal ? sourceY + (isTopOrLeft ? -perpOffset : perpOffset) : sourceY;
    // Pull target control point inward along the flow axis
    const flowOffset = 80;
    const targetCtrlX = isHorizontal ? targetX - flowOffset : targetX;
    const targetCtrlY = isHorizontal ? targetY : targetY - flowOffset;

    edgePath = `M ${sourceX},${sourceY} C ${sourceCtrlX},${sourceCtrlY} ${targetCtrlX},${targetCtrlY} ${targetX},${targetY}`;
    labelX = targetX;
    labelY = targetY;
  } else {
    // Force the edge to be perfectly straight by aligning the target coordinate
    // with the source coordinate on the axis that should be constant.
    const alignedTargetX = isHorizontal ? targetX : sourceX;
    const alignedTargetY = isHorizontal ? sourceY : targetY;
    const [path] = getStraightPath({
      sourceX,
      sourceY,
      targetX: alignedTargetX,
      targetY: alignedTargetY,
    });
    edgePath = path;
    labelX = alignedTargetX;
    labelY = alignedTargetY;
  }

  const handleAddClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      data?.onAddClick?.(source);
    },
    [data, source]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        data?.onAddClick?.(source);
      }
    },
    [data, source]
  );

  const handleAddAgent = useCallback(() => {
    data?.onAddAgentClick?.(source);
  }, [data, source]);

  // Reveal the agent button while the wrapper has mouse hover or keyboard
  // focus. Tracked separately so leaving the mouse doesn't collapse the
  // affordance while focus is still on the agent button.
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setIsFocused(false);
    }
  }, []);

  let strokeColor = tokens.colorNeutralStroke1;
  if (isTrueBranch) {
    strokeColor = tokens.colorPaletteGreenBorder2;
  } else if (isFalseBranch) {
    strokeColor = tokens.colorPaletteRedBorder2;
  } else if (isLoopBranch) {
    strokeColor = tokens.colorBrandStroke1;
  }

  const edgeStyle: React.CSSProperties = {
    ...style,
    strokeWidth: 2,
    stroke: strokeColor,
    strokeDasharray: '5,5',
    pointerEvents: 'all',
  };

  let addAriaLabel = 'Add an action at the end of the workflow';
  if (isTrueBranch) {
    addAriaLabel = 'Add action to True branch';
  } else if (isFalseBranch) {
    addAriaLabel = 'Add action to False branch';
  } else if (isLoopBranch) {
    addAriaLabel = 'Add action to loop body';
  }

  const buttonClass = mergeClasses(
    styles.addButton,
    isTrueBranch && styles.addButtonTrue,
    isFalseBranch && styles.addButtonFalse,
    isLoopBranch && styles.addButtonLoop,
  );
  const iconClass = mergeClasses(
    styles.addIcon,
    isTrueBranch && styles.addIconTrue,
    isFalseBranch && styles.addIconFalse,
    isLoopBranch && styles.addIconLoop,
  );

  return (
    <>
      <BaseEdge
        path={edgePath}
        style={edgeStyle}
        interactionWidth={20}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
          onMouseEnter={showAgentVariant ? handleMouseEnter : undefined}
          onMouseLeave={showAgentVariant ? handleMouseLeave : undefined}
          onFocus={showAgentVariant ? handleFocus : undefined}
          onBlur={showAgentVariant ? handleBlur : undefined}
        >
          <Tooltip content="Add an action" relationship="label">
            <button
              type="button"
              className={buttonClass}
              onClick={handleAddClick}
              onKeyDown={handleKeyDown}
              aria-label={addAriaLabel}
            >
              <AddFilled className={iconClass} aria-hidden="true" />
            </button>
          </Tooltip>
          {showAgentVariant && (
            <>
              <span
                aria-hidden="true"
                className={mergeClasses(
                  styles.hoverBridge,
                  isAgentRevealed && styles.hoverBridgeActive,
                )}
              />
              <div className={styles.agentButtonAnchor}>
                <EdgePillButton
                  icon={<BotFilled className={styles.agentIcon} />}
                  tooltip="Add an agent"
                  ariaLabel="Add an agent at the end of the workflow"
                  isRevealed={isAgentRevealed}
                  onActivate={handleAddAgent}
                />
              </div>
            </>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default TerminalEdge;
